import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../UIKit/Spinner';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
  FormControl,
  FormGroup,
  FormLabel,
  MenuItem,
  Stack,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  KeyboardArrowDown as ExpandIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Button from '../../UIKit/Button';
import TextField from '../../UIKit/TextField';
import Select from '../../UIKit/Select';
import Accordion from '../../UIKit/Accordion';
import Switch from '../../UIKit/Switch';
// Модальное окно для редактирования гида (вся логика внутри)
export const EditOnboardingDialog = ({
  open,
  onClose,
  guideId,
  isNew = false,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const params = useParams();
  const id = guideId || params.id;
  const navigate = useNavigate();

  // Состояние формы
  const [formData, setFormData] = useState({
    name: { ru: '', en: '' },
    description: { ru: '', en: '' },
    steps: [],
    active: true,
    autostart: false,
    roles: ['student', 'teacher', 'admin'],
    page: { url: '*', tabId: '' },
  });
  const [loading, setLoading] = useState(!isNew && open);
  const [saving, setSaving] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);

  // Загрузка данных существующего гида
  useEffect(() => {
    if (!open) return;
    const fetchGuide = async () => {
      if (isNew) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await onboardingAPI.admin.getGuideById(id);
        if (response.data) {
          const sortedSteps = [...response.data.steps].sort(
            (a, b) => a.order - b.order
          );
          setFormData({
            ...response.data,
            name: response.data.name || { ru: '', en: '' },
            description: response.data.description || { ru: '', en: '' },
            steps: sortedSteps,
            page: response.data.page || { url: '*', tabId: '' },
            roles: response.data.roles || ['student', 'teacher', 'admin'],
            active:
              response.data.active !== undefined ? response.data.active : true,
            autostart: response.data.autostart || false,
          });
          setLoading(false);
        } else {
          console.error('Ошибка загрузки гида: ответ пустой или некорректный');
          toast.error(t('admin:onboarding.loadError'));
          setLoading(false);
        }
      } catch (error) {
        console.error('Ошибка при загрузке гида:', error);
        toast.error(t('admin:onboarding.loadError'));
        setLoading(false);
      }
    };
    fetchGuide();
  }, [id, isNew, open, t]);

  // Сброс формы при открытии для нового гида
  useEffect(() => {
    if (open && isNew) {
      setFormData({
        name: { ru: '', en: '' },
        description: { ru: '', en: '' },
        steps: [],
        active: true,
        autostart: false,
        roles: ['student', 'teacher', 'admin'],
        page: { url: '*', tabId: '' },
      });
      setExpandedStep(null);
      setLoading(false);
    }
  }, [open, isNew]);

  // Обработчики формы
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handlePageChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, page: { ...prev.page, [name]: value } }));
  };
  const handleRoleToggle = (role) => {
    setFormData((prev) => {
      const currentRoles = [...prev.roles];
      if (currentRoles.includes(role)) {
        return { ...prev, roles: currentRoles.filter((r) => r !== role) };
      } else {
        return { ...prev, roles: [...currentRoles, role] };
      }
    });
  };
  const handleAddStep = () => {
    setFormData((prev) => {
      const newStepOrder = prev.steps.length;
      return {
        ...prev,
        steps: [
          ...prev.steps,
          {
            title: { ru: 'Новый шаг', en: 'New Step' },
            content: { ru: 'Содержимое нового шага', en: 'New Step Content' },
            selector: 'body',
            placement: 'bottom',
            route: '*',
            order: newStepOrder,
          },
        ],
      };
    });
    setExpandedStep(formData.steps.length);
  };
  const handleDeleteStep = (index) => {
    if (!window.confirm(t('admin:onboarding.confirmDeleteStep'))) return;
    setFormData((prev) => {
      const updatedSteps = prev.steps.filter((_, i) => i !== index);
      updatedSteps.forEach((step, i) => {
        step.order = i;
      });
      return { ...prev, steps: updatedSteps };
    });
    if (expandedStep === index) {
      setExpandedStep(null);
    } else if (expandedStep > index) {
      setExpandedStep(expandedStep - 1);
    }
  };
  const handleStepChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedSteps = [...prev.steps];
      updatedSteps[index] = { ...updatedSteps[index], [field]: value };
      return { ...prev, steps: updatedSteps };
    });
  };
  const handleMoveStep = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.steps.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    setFormData((prev) => {
      const updatedSteps = [...prev.steps];
      const [removed] = updatedSteps.splice(index, 1);
      updatedSteps.splice(newIndex, 0, removed);
      updatedSteps.forEach((step, i) => {
        step.order = i;
      });
      return { ...prev, steps: updatedSteps };
    });
    if (expandedStep === index) {
      setExpandedStep(newIndex);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.ru?.trim() || !formData.name?.en?.trim()) {
      toast.error(t('admin:onboarding.nameRequired'));
      return;
    }
    if (
      !formData.description?.ru?.trim() ||
      !formData.description?.en?.trim()
    ) {
      toast.error(t('admin:onboarding.descriptionRequired'));
      return;
    }
    if (formData.steps.length === 0) {
      toast.error(t('admin:onboarding.stepsRequired'));
      return;
    }
    // Проверяем, что у каждого шага заполнены оба языка
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (!step.title?.ru?.trim() || !step.title?.en?.trim()) {
        toast.error(t('admin:onboarding.stepTitleRequired', { index: i + 1 }));
        return;
      }
      if (!step.content?.ru?.trim() || !step.content?.en?.trim()) {
        toast.error(
          t('admin:onboarding.stepContentRequired', { index: i + 1 })
        );
        return;
      }
    }
    try {
      setSaving(true);
      if (isNew) {
        await onboardingAPI.admin.createGuide(formData);
        toast.success(t('admin:onboarding.createSuccess'));
      } else {
        await onboardingAPI.admin.updateGuide(id, formData);
        toast.success(t('admin:onboarding.updateSuccess'));
      }
      setSaving(false);
      if (onClose) {
        onClose();
      } else {
        navigate('/admin/onboarding');
      }
    } catch (error) {
      console.error('Ошибка при сохранении гида:', error);
      toast.error(t('admin:onboarding.saveError'));
      setSaving(false);
    }
  };
  const getPlacementOptions = () => [
    { value: 'top', label: 'top' },
    { value: 'top-start', label: 'top-start' },
    { value: 'top-end', label: 'top-end' },
    { value: 'bottom', label: 'bottom' },
    { value: 'bottom-start', label: 'bottom-start' },
    { value: 'bottom-end', label: 'bottom-end' },
    { value: 'left', label: 'left' },
    { value: 'left-start', label: 'left-start' },
    { value: 'left-end', label: 'left-end' },
    { value: 'right', label: 'right' },
    { value: 'right-start', label: 'right-start' },
    { value: 'right-end', label: 'right-end' },
    { value: 'auto', label: 'auto' },
    { value: 'auto-start', label: 'auto-start' },
    { value: 'auto-end', label: 'auto-end' },
    { value: 'center', label: 'center' },
  ];

  // --- UI ---
  const content = (
    <Container>
      <Box py={3}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          id="onboarding-editor-form"
        >
          <Grid container spacing={4}>
            {/* Левая колонка - основные настройки */}
            <Grid item xs={12} md={5}>
              <Typography variant="h6" mb={2}>
                {t('admin:onboarding.generalSettings')}
              </Typography>
              <Stack spacing={3}>
                <TextField
                  id="name-ru"
                  name="name-ru"
                  label={t('admin:onboarding.name') + ' (ru)'}
                  value={formData.name?.ru || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: { ...prev.name, ru: e.target.value },
                    }))
                  }
                  fullWidth
                  required
                />
                <TextField
                  id="name-en"
                  name="name-en"
                  label={t('admin:onboarding.name') + ' (en)'}
                  value={formData.name?.en || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value },
                    }))
                  }
                  fullWidth
                  required
                />
                <TextField
                  id="description-ru"
                  name="description-ru"
                  label={t('admin:onboarding.description') + ' (ru)'}
                  value={formData.description?.ru || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: { ...prev.description, ru: e.target.value },
                    }))
                  }
                  fullWidth
                  multiline
                  rows={3}
                  required
                />
                <TextField
                  id="description-en"
                  name="description-en"
                  label={t('admin:onboarding.description') + ' (en)'}
                  value={formData.description?.en || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: { ...prev.description, en: e.target.value },
                    }))
                  }
                  fullWidth
                  multiline
                  rows={3}
                  required
                />
                <TextField
                  id="page-url"
                  name="url"
                  label={t('admin:onboarding.pageUrl')}
                  value={formData.page?.url || ''}
                  onChange={handlePageChange}
                  placeholder="/profile, /courses, *"
                  fullWidth
                  helperText={t('admin:onboarding.pageUrlHelp')}
                />
                <TextField
                  id="page-tabId"
                  name="tabId"
                  label={t('admin:onboarding.pageTab')}
                  value={formData.page?.tabId || ''}
                  onChange={handlePageChange}
                  placeholder="0, 1, settings, ..."
                  fullWidth
                  helperText={t('admin:onboarding.pageTabHelp')}
                />
                <FormGroup sx={{ gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="active"
                        checked={formData.active}
                        onChange={handleInputChange}
                        color="success"
                        sx={{ mx: 1 }}
                      />
                    }
                    label={t('admin:onboarding.isActive')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        name="autostart"
                        checked={formData.autostart}
                        onChange={handleInputChange}
                        color="primary"
                        sx={{ mx: 1 }}
                      />
                    }
                    label={t('admin:onboarding.autostart')}
                  />
                </FormGroup>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {t('admin:onboarding.roles')}
                  </FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.roles.includes('student')}
                          onChange={() => handleRoleToggle('student')}
                        />
                      }
                      label={t('admin:onboarding.roleStudent')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.roles.includes('teacher')}
                          onChange={() => handleRoleToggle('teacher')}
                        />
                      }
                      label={t('admin:onboarding.roleTeacher')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.roles.includes('admin')}
                          onChange={() => handleRoleToggle('admin')}
                        />
                      }
                      label={t('admin:onboarding.roleAdmin')}
                    />
                  </FormGroup>
                </FormControl>
              </Stack>
            </Grid>
            {/* Правая колонка - шаги */}
            <Grid item xs={12} md={7}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="h6">
                  {t('admin:onboarding.steps')}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddStep}
                >
                  {t('admin:onboarding.addStep')}
                </Button>
              </Box>
              {formData.steps.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('admin:onboarding.noSteps')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddStep}
                    sx={{ mt: 2 }}
                  >
                    {t('admin:onboarding.addFirstStep')}
                  </Button>
                </Box>
              ) : (
                <>
                  {formData.steps.map((step, index) => (
                    <Accordion
                      elevation={0}
                      key={index}
                      expanded={expandedStep === index}
                      onChange={() =>
                        setExpandedStep(expandedStep === index ? null : index)
                      }
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandIcon />}
                        aria-controls={`step-${index}-content`}
                        id={`step-${index}-header`}
                      >
                        <Box display="flex" alignItems="center" width="100%">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveStep(index, 'up');
                            }}
                            disabled={index === 0}
                            sx={{ mr: 1 }}
                          >
                            <UpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveStep(index, 'down');
                            }}
                            disabled={index === formData.steps.length - 1}
                            sx={{ mr: 1 }}
                          >
                            <DownIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            {(step.title.ru ||
                              t('admin:onboarding.untitledStep')) +
                              (step.title.en ? ` / ${step.title.en}` : '')}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteStep(index);
                            }}
                            color="error"
                            sx={{ ml: 2 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={3}>
                          <Box>
                            <Stack spacing={2}>
                              <TextField
                                label={
                                  t('admin:onboarding.stepTitle') + ' (ru)'
                                }
                                value={step.title?.ru || ''}
                                onChange={(e) =>
                                  handleStepChange(index, 'title', {
                                    ...step.title,
                                    ru: e.target.value,
                                  })
                                }
                                fullWidth
                                required
                              />
                              <TextField
                                label={
                                  t('admin:onboarding.stepContent') + ' (ru)'
                                }
                                value={step.content?.ru || ''}
                                onChange={(e) =>
                                  handleStepChange(index, 'content', {
                                    ...step.content,
                                    ru: e.target.value,
                                  })
                                }
                                fullWidth
                                multiline
                                rows={4}
                                required
                              />
                              <TextField
                                label={
                                  t('admin:onboarding.stepTitle') + ' (en)'
                                }
                                value={step.title?.en || ''}
                                onChange={(e) =>
                                  handleStepChange(index, 'title', {
                                    ...step.title,
                                    en: e.target.value,
                                  })
                                }
                                fullWidth
                                required
                              />

                              <TextField
                                label={
                                  t('admin:onboarding.stepContent') + ' (en)'
                                }
                                value={step.content?.en || ''}
                                onChange={(e) =>
                                  handleStepChange(index, 'content', {
                                    ...step.content,
                                    en: e.target.value,
                                  })
                                }
                                fullWidth
                                multiline
                                rows={4}
                                required
                              />
                            </Stack>
                          </Box>
                          <TextField
                            label={t('admin:onboarding.stepSelector')}
                            value={step.selector || ''}
                            onChange={(e) =>
                              handleStepChange(
                                index,
                                'selector',
                                e.target.value
                              )
                            }
                            fullWidth
                            placeholder=".element-class, #element-id"
                            helperText={t('admin:onboarding.selectorHelp')}
                          />
                          <FormControl fullWidth>
                            <Select
                              labelId={`placement-label-${index}`}
                              value={step.placement || 'bottom'}
                              label={t('admin:onboarding.stepPlacement')}
                              onChange={(e) =>
                                handleStepChange(
                                  index,
                                  'placement',
                                  e.target.value
                                )
                              }
                            >
                              {getPlacementOptions().map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label={t('admin:onboarding.stepRoute')}
                            value={step.route || '*'}
                            onChange={(e) =>
                              handleStepChange(index, 'route', e.target.value)
                            }
                            fullWidth
                            placeholder="/dashboard/*, /courses/:id"
                            helperText={t('admin:onboarding.routeHelp')}
                          />
                          {/* Роли для шага */}
                          <FormControl component="fieldset">
                            <FormLabel component="legend">
                              {t('admin:onboarding.stepRoles')}
                            </FormLabel>
                            <FormGroup row>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={step.roles?.includes('student')}
                                    onChange={() => {
                                      const roles = step.roles || [];
                                      handleStepChange(
                                        index,
                                        'roles',
                                        roles.includes('student')
                                          ? roles.filter((r) => r !== 'student')
                                          : [...roles, 'student']
                                      );
                                    }}
                                  />
                                }
                                label={t('admin:onboarding.roleStudent')}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={step.roles?.includes('teacher')}
                                    onChange={() => {
                                      const roles = step.roles || [];
                                      handleStepChange(
                                        index,
                                        'roles',
                                        roles.includes('teacher')
                                          ? roles.filter((r) => r !== 'teacher')
                                          : [...roles, 'teacher']
                                      );
                                    }}
                                  />
                                }
                                label={t('admin:onboarding.roleTeacher')}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={step.roles?.includes('admin')}
                                    onChange={() => {
                                      const roles = step.roles || [];
                                      handleStepChange(
                                        index,
                                        'roles',
                                        roles.includes('admin')
                                          ? roles.filter((r) => r !== 'admin')
                                          : [...roles, 'admin']
                                      );
                                    }}
                                  />
                                }
                                label={t('admin:onboarding.roleAdmin')}
                              />
                            </FormGroup>
                          </FormControl>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {isNew
              ? t('admin:onboarding.createTitle')
              : t('admin:onboarding.editTitle')}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>{loading ? <Spinner /> : content}</DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={() => (onClose ? onClose() : navigate('/admin/onboarding'))}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="submit"
          form="onboarding-editor-form"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={saving}
        >
          {saving ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
